import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AuthService } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { MediaType } from '@klickdata/core/media/src/media-type';
import { Media } from '@klickdata/core/media/src/media.model';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { S3MediaService } from '@klickdata/core/media/src/s3-media.service';
import { MessageDeletedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceMediaTypes } from '@klickdata/core/question/src/question-type/question-type';
import { AppScope, ResourceTypes } from '@klickdata/core/resource';
import { MaterialModelType } from '@klickdata/core/resource/src/resource.model';
import { ResourceService, YouTubeVideoData } from '@klickdata/core/resource/src/resource.service';
import { User } from '@klickdata/core/user';
import { PromptType, Utils } from '@klickdata/core/util';
import moment from 'moment';
import {
    BehaviorSubject,
    catchError,
    debounceTime,
    defer,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';

@Component({
    selector: 'app-video-link-control',
    templateUrl: './video-link-control.component.html',
    styleUrls: ['./video-link-control.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => VideoLinkControlComponent),
            multi: true,
        },
    ],
})
export class VideoLinkControlComponent implements ControlValueAccessor, OnDestroy, OnInit {
    public videoTypeControl = new FormControl('file');
    public linkControl = new FormControl('');
    public videoMedia: Media;
    public directPlaybackMedia: Media;
    public progress$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    public onSavingFile: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private destroy: Subject<boolean> = new Subject<boolean>();
    MediaType = MediaType;
    public loadingUploader: Subject<boolean> = new Subject<boolean>();
    public isAIUploading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @Output() mediaUploaded = new EventEmitter<Media>();
    @Output() mediaCleared = new EventEmitter<void>();
    @Input() uploaderBtnLabel = $localize`Upload File`;
    @Input() radioLabel = $localize`Video Source`;
    @Input() scope: AppScope = AppScope.RESOURCES;
    @Input() _mediaId: number;
    @Input() resourceId: number;
    @Input() typeId: ResourceTypes;
    @Input() hasHiddenCopyLink: boolean = true;
    @Input() hasHiddenDeleteMedia: boolean = true;
    @Input() canRemoveMedia: boolean = true;
    public authUser$: Observable<User>;
    @Input() mediaPromptStateKey: PromptType = 'video/trailer';
    @Input() mediaKind: 'trailer' | 'presentation' = 'trailer';
    mvp2 = true;
    Utils = Utils;
    get mediaId(): number {
        return this._mediaId;
    }
    set mediaId(mediaId: number) {
        this._mediaId = mediaId;
        this.propagateChange(this._mediaId);
    }
    @Input() dropSize = '200px';
    public get dropStyle() {
        return {
            height: this.dropSize,
            width: this.dropSize,
            flex: `0 1 ${this.dropSize}`,
        };
    }
    public isLoadingVideoInfo: boolean;
    public isCheckingLink = false;
    public youTubeVideoInfo$: Observable<YouTubeVideoData>;
    private readonly pastedVideoTypes = ['video/html5', 'video/youtube', 'video/vimeo'];
    constructor(
        protected mediaService: MediaService,
        protected cdr: ChangeDetectorRef,
        protected auth: AuthService,
        protected mobileService: MobileService,
        protected messageService: MessageService,
        protected s3MediaService: S3MediaService,
        protected config: ConfigService,
        protected http: HttpClient,
        protected resourceService: ResourceService
    ) {}
    ngOnInit(): void {
        this.authUser$ = this.auth.getUser();
        this.linkControl.valueChanges
            .pipe(
                takeUntil(this.destroy),
                filter((link) => Utils.isVideoUrl(link) && Utils.isUrl(link))
            )
            .subscribe((link) => {
                this.getLinkInfo(link);
            });
        this.videoTypeControl.valueChanges.pipe(takeUntil(this.destroy)).subscribe((value) => {
            this.linkControl.patchValue('');
        });
        this.linkControl.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this.destroy),
                switchMap((link) => this.savePastedLink(link))
            )
            .subscribe((media) => {
                if (media) {
                    this.keepUploadedLink(media);
                }
            });
    }
    public onChange(e: any): void {
        this.mediaId = e.value;
        if (!!this.resourceId) {
            const processing$ =
                this.mediaKind === 'trailer'
                    ? this.resourceService.getIsTrailerProcessing()
                    : this.resourceService.getIsPresentationProcessing();

            processing$
                .pipe(
                    takeUntil(this.destroy),
                    filter((data) => !!data?.resourceId && data.resourceId === this.resourceId)
                )
                .subscribe((data) => this.isAIUploading$.next(data.processing));
        }
    }

    public writeValue(mediaId: any): void {
        this._mediaId = mediaId;
        if (mediaId) {
            this.mediaService
                .getMedia(mediaId)
                .pipe(takeUntil(this.destroy))
                .subscribe((media) => this.restoreSavedMedia(media));
        }
    }

    public clearMedia() {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete video`,
                contentBody: $localize`You are about to delete this video. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(filter((action) => action === SideNaveActionsTypes.POSITIVE))
            .subscribe((action) => {
                if (action) {
                    this.videoMedia = null;
                    this.directPlaybackMedia = null;
                    this.mediaId = null;
                    this.propagateChange(this.mediaId);
                    this.progress$.next(null);
                    this.mediaCleared.emit();
                    this.cdr.markForCheck();
                    this.messageService.openMessage(MessageDeletedComponent);
                }
            });
    }
    goToLink(url: string) {
        if (Utils.isUrl(url)) {
            window.open(url, '_blank');
        }
    }

    public propagateChange = (_: any) => {};

    public registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    public registerOnTouched(fn: any): void {}
    public ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
    public addMedia(errorMsg?: string) {
        const isPresentation = this.mediaKind === 'presentation';
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'video_library',
                title: isPresentation ? $localize`Add a presentation` : $localize`Add a trailer`,
                description: isPresentation
                    ? $localize`Please select your preferred way to add a presentation.`
                    : $localize`Please select your preferred way to add a trailer.`,
                uploadBtnLabel: isPresentation ? $localize`Upload presentation` : $localize`Upload trailer`,
                uploadBtnTooltip: isPresentation ? $localize`Upload presentation` : $localize`Upload trailer`,
                libraryBtnLabel: $localize`Video library`,
                libraryBtnTooltip: $localize`Add video from library`,
                aiBtnLabel: isPresentation ? $localize`Create Presentation` : $localize`Create Trailer`,
                disableAI: false,
                errorMsg: errorMsg,
                columns: ['filename', 'min_tools'],
                type: this.mediaKind,
                mediaType: 'video',
                allowedMimeType: ['video/mp4', 'video/webm', 'video/3gpp'],
            },
        });
        this.getMediaTypeFromSlideIn();
    }
    private getMediaTypeFromSlideIn() {
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === this.mediaKind && !!data.value),
                tap((data) => {
                    if (data.value != ResourceMediaTypes.FILE) {
                        this.isAIUploading$.next(true);
                    }
                }),
                switchMap((data) =>
                    data.value === ResourceMediaTypes.FILE
                        ? this.mediaService.getMedia(data.mediaId)
                        : this.mediaService.createAIMedia(
                              null,
                              data.aiText,
                              this.mediaKind === 'presentation'
                                  ? MaterialModelType.PRESENTATION_VIDEO
                                  : MaterialModelType.TRAILER_VIDEO
                          )
                ),
                filter((data) => !!data),
                takeUntil(this.destroy),
                catchError((err) => {
                    this.handleAIRequestFailure(err);
                    return of(null);
                })
            )
            .subscribe((media: Media) => {
                this.videoMedia = media;
                this.mediaId = Number(media.id);
                this.mediaUploaded.emit(media);
                this.propagateChange(media.id);
                this.cdr.markForCheck();
            });
    }
    private savePastedLink(link: string): Observable<Media | null> {
        const trimmedLink = link?.trim() ?? '';
        if (!trimmedLink) {
            this.clearMedia();
            return of(null);
        }
        if (!Utils.isUrl(trimmedLink)) {
            return of(null);
        }
        return defer(() => {
            this.isCheckingLink = true;
            this.cdr.markForCheck();
            if (Utils.isVideoUrl(trimmedLink)) {
                return this.uploadLink(trimmedLink);
            }
            return this.uploadDirectLink(trimmedLink);
        }).pipe(
            finalize(() => {
                this.isCheckingLink = false;
                this.cdr.markForCheck();
            })
        );
    }

    private uploadDirectLink(link: string): Observable<Media | null> {
        return this.readDirectVideoMime(link).pipe(
            switchMap((mime) => {
                if (!mime) {
                    this.messageService.openMessage(MessageErrorComponent, $localize`This link is not a video.`);
                    return of(null);
                }
                this.directPlaybackMedia = this.playbackMedia(link, mime);
                this.cdr.markForCheck();
                return this.uploadLink(link);
            })
        );
    }

    private uploadLink(link: string): Observable<Media | null> {
        if (Utils.isVideoUrl(link)) {
            this.directPlaybackMedia = null;
        }
        return this.mediaService
            .uploadMediaByLink({
                link,
                type: 'video/' + Utils.getMediaProvider(link),
                scope_id: ResourceTypes.scopeByChildType(this.typeId),
            })
            .pipe(
                catchError((error: unknown) => {
                    this.showLinkSaveError(error);
                    return of(null);
                })
            );
    }

    /**
     * HttpClient adds app headers that this file host rejects, so the type is read directly.
     */
    private readDirectVideoMime(link: string): Observable<string | null> {
        return new Observable<string | null>((observer) => {
            const abort = new AbortController();
            fetch(link, { method: 'HEAD', signal: abort.signal })
                .then((response) => {
                    const mime = (response.headers.get('Content-Type') ?? '').split(';')[0].trim().toLowerCase();
                    observer.next(response.ok && mime.startsWith('video/') ? mime : null);
                    observer.complete();
                })
                .catch(() => {
                    if (!abort.signal.aborted) {
                        observer.next(null);
                    }
                    observer.complete();
                });
            return () => abort.abort();
        });
    }

    private keepUploadedLink(media: Media): void {
        if (!media.url && !media.src) {
            media.url = this.linkControl.value;
        }
        if (!Utils.isYoutubeURL(this.linkControl.value)) {
            this.youTubeVideoInfo$ = of(null);
            this.isLoadingVideoInfo = false;
        }
        this.videoMedia = media;
        this.mediaId = Number(media.id);
        this.mediaUploaded.emit(media);
        this.cdr.markForCheck();
    }

    private restoreSavedMedia(media: Media): void {
        const source = media?.link || media?.url || media?.src || '';
        const savedAsLink = !!source && (Utils.isVideoUrl(source) || this.pastedVideoTypes.includes(media?.type));
        this.videoMedia = media;
        this.videoTypeControl.patchValue(savedAsLink ? 'link' : 'file', { emitEvent: false });
        if (savedAsLink) {
            this.linkControl.patchValue(source, { emitEvent: false });
            this.getLinkInfo(source);
            this.previewSavedDirectLink(media, source);
        }
        this.cdr.markForCheck();
    }

    private previewSavedDirectLink(media: Media, source: string): void {
        if (Utils.isVideoUrl(source)) {
            this.directPlaybackMedia = null;
            return;
        }
        const storedMime = (media?.type ?? '').split(';')[0].trim().toLowerCase();
        if (storedMime.startsWith('video/') && storedMime !== 'video/html5') {
            this.directPlaybackMedia = this.playbackMedia(source, storedMime);
            return;
        }
        this.readDirectVideoMime(source)
            .pipe(takeUntil(this.destroy))
            .subscribe((mime) => {
                if (mime) {
                    this.directPlaybackMedia = this.playbackMedia(source, mime);
                    this.cdr.markForCheck();
                }
            });
    }

    private playbackMedia(url: string, mime: string): Media {
        return new Media({ url, type: mime, provider: 'html5' });
    }

    private showLinkSaveError(error: unknown): void {
        const messages = error instanceof HttpErrorResponse ? error.error?.error?.messages : null;
        const text =
            Array.isArray(messages) && messages.length
                ? messages.join('\n')
                : $localize`Something went wrong while saving. Please try again.`;
        this.messageService.openMessage(MessageErrorComponent, text);
    }

    private getLinkInfo(link: string) {
        if (Utils.isYoutubeURL(link)) {
            this.isLoadingVideoInfo = true;
            const apiUrl = `https://www.googleapis.com/youtube/v3/videos?id=${Utils.getYoutubeMedia(link).src}&key=${
                this.config.config.YOUTUBE_DATA_API_KEY
            }&part=snippet,contentDetails`;
            this.youTubeVideoInfo$ = this.http.get<any>(apiUrl).pipe(
                map((res) => {
                    if (res?.items?.length) {
                        return res.items[0];
                    } else {
                        this.isLoadingVideoInfo = false;
                        return null;
                    }
                }),
                filter((res) => !!res),
                map((item) => {
                    this.isLoadingVideoInfo = false;
                    return {
                        title: item.snippet.title,
                        duration: moment.duration(item.contentDetails.duration).asSeconds().toString(),
                    };
                })
            );
        } else {
            this.youTubeVideoInfo$ = of(null);
        }
    }
    handleAIRequestFailure(err) {
        if (err && err.error && err.error.error) {
            this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'));
            this.addMedia(err.error.error.messages.join('/n'));
        }
    }
}
